import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  Target, 
  CheckCircle, 
  Circle,
  RotateCcw,
  Save,
  ZoomIn,
  ZoomOut,
  Maximize,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface FileUpload {
  file: File | null;
  isValid: boolean;
  errorMessage: string;
}

interface DataPoint {
  featureLabel: string;
  x: number;
  y: number;
  z: number;
  page?: number; // Page number where this marker should be visible
}

interface NominalInfo {
  featureLabel: string;
  characteristic: string;
  nominal: number;
  upperTolerance: number;
  lowerTolerance: number;
}

interface MarkerPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageFiles: FileUpload[];  // Changed to array for multiple images
  nominalInfo: NominalInfo[];
  existingMarkers: DataPoint[];
  onMarkersUpdated: (markers: DataPoint[]) => void;
}

const MarkerPickerModal: React.FC<MarkerPickerModalProps> = ({
  isOpen,
  onClose,
  imageFiles,
  nominalInfo,
  existingMarkers,
  onMarkersUpdated
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [markers, setMarkers] = useState<{ [page: number]: DataPoint[] }>({});
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragStartTranslate, setDragStartTranslate] = useState({ x: 0, y: 0 });
  
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get the current image file based on page
  const currentImageFile = imageFiles[currentPage];

  // Load image when modal opens or page changes
  useEffect(() => {
    if (isOpen && currentImageFile && currentImageFile.isValid && currentImageFile.file) {
      const url = URL.createObjectURL(currentImageFile.file);
      setImageUrl(url);
      setImageLoaded(false);
      
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [isOpen, currentImageFile, currentPage]);

  // Reset zoom and pan when image loads
  useEffect(() => {
    if (imageLoaded) {
      handleFitToView();
    }
  }, [imageLoaded]);

  // Reset current page when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
    }
  }, [isOpen]);

  // Initialize markers if not already set
  useEffect(() => {
    if (isOpen && Object.keys(markers).length === 0) {
      const emptyMarkers = nominalInfo.map(nominal => ({
        featureLabel: nominal.featureLabel,
        x: 0,
        y: 0,
        z: 0
      }));
      
      // Initialize all pages with empty markers
      const pageMarkers: { [page: number]: DataPoint[] } = {};
      imageFiles.forEach((_, index) => {
        pageMarkers[index] = [...emptyMarkers];
      });
      
      setMarkers(pageMarkers);
    }
  }, [isOpen, nominalInfo, imageFiles]);

  const handleWheel = (event: React.WheelEvent) => {
    if (!imageRef.current || !containerRef.current) return;

    // Only prevent default if we're actually going to zoom
    event.preventDefault();

    const rect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Get mouse position relative to the image
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Calculate zoom
    const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * zoomFactor, 0.1), 5);
    
    // Calculate new translate to keep zoom centered on mouse
    const deltaScale = newScale / scale;
    const newTranslateX = translateX - (mouseX * (deltaScale - 1));
    const newTranslateY = translateY - (mouseY * (deltaScale - 1));
    
    setScale(newScale);
    setTranslateX(newTranslateX);
    setTranslateY(newTranslateY);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    // Only start drag if shift is held and not clicking on the image for marker placement
    if (!event.shiftKey || event.target === imageRef.current) return;
    
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
    setDragStartTranslate({ x: translateX, y: translateY });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = event.clientX - dragStart.x;
    const deltaY = event.clientY - dragStart.y;
    
    setTranslateX(dragStartTranslate.x + deltaX);
    setTranslateY(dragStartTranslate.y + deltaY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5);
    setScale(newScale);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale * 0.8, 0.1);
    setScale(newScale);
  };

  const handleFitToView = () => {
    if (!imageRef.current || !containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth - 32; // Account for padding
    const containerHeight = container.clientHeight - 32;
    
    const imageWidth = imageRef.current.naturalWidth;
    const imageHeight = imageRef.current.naturalHeight;
    
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;
    const fitScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 100%
    
    setScale(fitScale);
    setTranslateX((containerWidth - imageWidth * fitScale) / 2);
    setTranslateY((containerHeight - imageHeight * fitScale) / 2);
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!imageRef.current || !containerRef.current || currentFeatureIndex >= nominalInfo.length) return;

    // Don't place marker if shift is held (panning mode)
    if (event.shiftKey) return;

    const rect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Calculate click position relative to the container
    const clickX = event.clientX - containerRect.left;
    const clickY = event.clientY - containerRect.top;
    
    // Account for the image's transform (translate and scale)
    // Convert from container coordinates to original image coordinates
    const imageX = (clickX - translateX) / scale;
    const imageY = (clickY - translateY) / scale;
    
    // Ensure click is within image bounds
    if (imageX < 0 || imageY < 0 || imageX > imageRef.current.naturalWidth || imageY > imageRef.current.naturalHeight) return;

    // Update the marker for the current feature
    const currentFeature = nominalInfo[currentFeatureIndex];
    const updatedMarkers = [...markers[currentPage] || []];
    const markerIndex = updatedMarkers.findIndex(m => m.featureLabel === currentFeature.featureLabel);
    
    if (markerIndex >= 0) {
      updatedMarkers[markerIndex] = {
        featureLabel: currentFeature.featureLabel,
        x: Math.round(imageX),
        y: Math.round(imageY),
        z: 0,
        page: currentPage // Include the current page
      };
    }

    setMarkers(prev => ({ ...prev, [currentPage]: updatedMarkers }));
    
    // Auto-advance to next feature
    if (currentFeatureIndex < nominalInfo.length - 1) {
      setCurrentFeatureIndex(currentFeatureIndex + 1);
    }
  };

  const handleFeatureSelect = (index: number) => {
    setCurrentFeatureIndex(index);
  };

  const handleReset = () => {
    const emptyMarkers = nominalInfo.map(nominal => ({
      featureLabel: nominal.featureLabel,
      x: 0,
      y: 0,
      z: 0,
      page: currentPage // Include the current page
    }));
    setMarkers(prev => ({ ...prev, [currentPage]: emptyMarkers }));
    setCurrentFeatureIndex(0);
  };

  const handleSave = () => {
    // Collect unique markers (one per feature) from all pages
    const uniqueMarkers: DataPoint[] = [];
    const seenFeatures = new Set<string>();
    
    // Go through all pages and collect the first positioned marker for each feature
    Object.values(markers).forEach(pageMarkers => {
      pageMarkers.forEach(marker => {
        // Only add if this feature hasn't been seen before and the marker is positioned
        if (!seenFeatures.has(marker.featureLabel) && (marker.x !== 0 || marker.y !== 0)) {
          uniqueMarkers.push(marker);
          seenFeatures.add(marker.featureLabel);
        }
      });
    });
    
    onMarkersUpdated(uniqueMarkers);
    onClose();
  };

  const handleClose = () => {
    // Reset to existing markers if user cancels
    setMarkers({});
    setCurrentFeatureIndex(0);
    setCurrentPage(0);
    // Reset zoom and pan
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    onClose();
  };

  const isMarkerPositioned = (featureLabel: string) => {
    // Check if this feature is positioned on any page
    return Object.values(markers).some(pageMarkers => {
      const marker = pageMarkers.find(m => m.featureLabel === featureLabel);
      return marker && (marker.x !== 0 || marker.y !== 0);
    });
  };

  const getMarkerPosition = (featureLabel: string) => {
    // Find the first positioned marker for this feature across all pages
    for (const pageMarkers of Object.values(markers)) {
      const marker = pageMarkers.find(m => m.featureLabel === featureLabel);
      if (marker && (marker.x !== 0 || marker.y !== 0)) {
        return marker;
      }
    }
    return undefined;
  };

  const renderMarkerOnImage = (marker: DataPoint) => {
    if (!imageRef.current || !containerRef.current || marker.x === 0 && marker.y === 0) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Convert image coordinates to display coordinates accounting for zoom and pan
    const displayX = (marker.x * scale) + translateX;
    const displayY = (marker.y * scale) + translateY;

    const isCurrentFeature = nominalInfo[currentFeatureIndex]?.featureLabel === marker.featureLabel;

    return (
      <div
        key={marker.featureLabel}
        className={`absolute w-4 h-4 rounded-full border-2 transform -translate-x-2 -translate-y-2 pointer-events-none z-20 ${
          isCurrentFeature 
            ? 'bg-blue-500 border-blue-600 shadow-lg scale-125' 
            : 'bg-red-500 border-red-600'
        }`}
        style={{
          left: displayX,
          top: displayY,
        }}
      >
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-bold text-white bg-black bg-opacity-75 px-1 rounded whitespace-nowrap">
          {marker.featureLabel}
        </div>
      </div>
    );
  };

  const completedCount = (() => {
    const seenFeatures = new Set<string>();
    let count = 0;
    
    Object.values(markers).forEach(pageMarkers => {
      pageMarkers.forEach(marker => {
        if (!seenFeatures.has(marker.featureLabel) && (marker.x !== 0 || marker.y !== 0)) {
          count++;
          seenFeatures.add(marker.featureLabel);
        }
      });
    });
    
    return count;
  })();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Pick Markers on Image ({completedCount}/{nominalInfo.length})
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-hidden">
          {/* Feature List */}
          <div className="lg:col-span-1 space-y-2 overflow-y-auto max-h-full">
            <Label className="text-sm font-medium">Features to Mark:</Label>
            <div className="space-y-2">
              {nominalInfo.map((feature, index) => (
                <Card
                  key={feature.featureLabel}
                  className={`p-3 cursor-pointer transition-colors ${
                    index === currentFeatureIndex
                      ? 'ring-2 ring-blue-500 bg-blue-50'
                      : isMarkerPositioned(feature.featureLabel)
                      ? 'bg-green-50 border-green-200'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleFeatureSelect(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isMarkerPositioned(feature.featureLabel) ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium truncate">
                          {feature.featureLabel}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {feature.characteristic}
                      </div>
                      {isMarkerPositioned(feature.featureLabel) && (
                        <div className="text-xs text-green-600 mt-1">
                          Position: ({getMarkerPosition(feature.featureLabel)?.x}, {getMarkerPosition(feature.featureLabel)?.y})
                        </div>
                      )}
                    </div>
                    <Badge variant={index === currentFeatureIndex ? "default" : "secondary"}>
                      {index + 1}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Image Area */}
          <div className="lg:col-span-2 relative overflow-hidden max-h-full border rounded" ref={containerRef}>
            {/* Zoom Controls */}
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-1 bg-white/90 backdrop-blur-sm rounded p-1 shadow-lg">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                className="h-8 w-8 p-0"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                className="h-8 w-8 p-0"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFitToView}
                className="h-8 w-8 p-0"
                title="Fit to View"
              >
                <Maximize className="w-4 h-4" />
              </Button>
            </div>

            {/* Page Navigation */}
            {imageFiles.length > 1 && (
              <div className="absolute top-4 left-16 z-10 bg-white/90 backdrop-blur-sm rounded p-1 shadow-lg">
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="h-8 w-8 p-0"
                    title="Previous Page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground min-w-[3rem] text-center px-1">
                    {currentPage + 1} / {imageFiles.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(imageFiles.length - 1, prev + 1))}
                    disabled={currentPage === imageFiles.length - 1}
                    className="h-8 w-8 p-0"
                    title="Next Page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Zoom Level Indicator */}
            <div className="absolute top-4 left-4 z-10 bg-black/75 text-white px-2 py-1 rounded text-xs">
              {Math.round(scale * 100)}%
            </div>

            <div 
              className="relative w-full h-full overflow-hidden"
              style={{ cursor: isDragging ? 'grabbing' : 'default' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              {imageUrl && (
                <div className="relative inline-block">
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Marker positioning"
                    className="cursor-crosshair border rounded select-none"
                    onLoad={() => setImageLoaded(true)}
                    onClick={handleImageClick}
                    style={{
                      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                      transformOrigin: '0 0',
                      maxWidth: 'none',
                      maxHeight: 'none'
                    }}
                    draggable={false}
                  />
                  {(markers[currentPage] || []).map(marker => renderMarkerOnImage(marker))}
                </div>
              )}
              {!imageLoaded && (
                <div className="w-full h-64 bg-gray-200 rounded flex items-center justify-center">
                  <span className="text-gray-500">Loading image...</span>
                </div>
              )}
            </div>
            
            {/* Instructions */}
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm text-xs text-gray-600 px-2 py-1 rounded shadow-lg">
              <div>• Click: Place marker</div>
              <div>• Shift+Drag: Pan image</div>
              <div>• Wheel: Zoom in/out</div>
            </div>
            
            {imageLoaded && currentFeatureIndex < nominalInfo.length && (
              <div className="absolute bottom-4 left-4 bg-blue-600 text-white px-3 py-2 rounded shadow-lg">
                <div className="text-sm font-medium">
                  Click to mark: {nominalInfo[currentFeatureIndex]?.featureLabel}
                </div>
                <div className="text-xs opacity-90">
                  {nominalInfo[currentFeatureIndex]?.characteristic}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset All
            </Button>
            <span className="text-sm text-muted-foreground">
              {completedCount} of {nominalInfo.length} markers positioned
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={completedCount === 0}>
              <Save className="w-4 h-4 mr-2" />
              Save Markers ({completedCount})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkerPickerModal; 